import { personRef } from "@/content/author";
import { socialImageMeta } from "@/lib/social-sharing";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { InfoCard, MoreLink, PageIntro, PageSection, Prose, WRAP } from "@/components/site/page";
import { ENTRIES, type Lang } from "@/components/LexiconPage";
import { conceptSlugForPilot, isPilot, pilotSlug } from "@/lib/lexicon-pilot";
import { pageTitle, siteName } from "@/lib/brand";
import { PILOT_LONG_TR, renderInlineMarkdown } from "@/lib/lexicon-pilot-content";


const CATEGORY_LABEL = {
  yz: { tr: "Yapay Zeka", en: "AI" },
  surd: { tr: "Sürdürülebilirlik", en: "Sustainability" },
  insan: { tr: "İnsan & Gelecek", en: "Human & Future" },
  ilet: { tr: "İletişim", en: "Communication" },
  iklim: { tr: "İklim", en: "Climate" },
} as const;


export function LexiconDetail({ id, lang }: { id: string; lang: Lang }) {
  const entry = ENTRIES.find((e) => e.id === id);
  if (!entry) return null;

  // Aynı kategoriden 4 ilgili kavram (kendisi hariç)
  const related = ENTRIES.filter(
    (e) => (lang === "tr" || isPilot(e.id)) && e.id !== entry.id && e.cats.some((c) => entry.cats.includes(c)),
  ).slice(0, 4);

  const hubPath = lang === "tr" ? "/sozluk" : "/glossary";
  const hubLabel = lang === "tr" ? "Sözlük" : "Lexicon";
  const long = lang === "tr" ? PILOT_LONG_TR[entry.id] : undefined;
  const cats = entry.cats.map((c) => CATEGORY_LABEL[c][lang]).join(" · ");

  return (
    <>
      <PageIntro
        crumbs={[{ label: hubLabel, to: hubPath }, { label: entry.term[lang] }]}
        kicker={`${lang === "tr" ? "Kavram" : "Concept"} · ${cats}`}
        title={entry.term[lang]}
        lede={entry.short[lang]}
      />

      <section className="border-t border-ink/10">
        <div className={`${WRAP} grid gap-10 py-8 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:gap-14 lg:py-12`}>
          <Prose className="font-serif text-[18px] sm:text-[19px]">
            {long ? (
              <>
                {long.hook.trim() !== entry.short[lang].trim() && <blockquote className="border-l-2 border-ink pl-5 font-serif text-[21px] leading-snug text-ink sm:text-[24px]">{long.hook}</blockquote>}
                {long.paragraphs.map((p, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(p) }} />
                ))}
                {long.sections?.map((section) => (
                  <section key={section.heading} className="space-y-4 pt-4">
                    <h2 className="font-serif text-2xl font-medium text-ink sm:text-[28px]">{section.heading}</h2>
                    {section.paragraphs.map((p, i) => (
                      <p key={i} dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(p) }} />
                    ))}
                  </section>
                ))}
              </>
            ) : (
              <p>{entry.body[lang]}</p>
            )}
          </Prose>

          <aside className="space-y-6 self-start">
            {entry.related && entry.related.length > 0 && (
              <InfoCard title={lang === "tr" ? "Bu kavramı geliştiren yazılar" : "Essays that develop this concept"}>
                <ul className="divide-y divide-ink/10">
                  {entry.related.map((r) => (
                    <li key={r.slug}>
                      {r.to === "dergi" ? (
                        <Link to="/dergi/$slug" params={{ slug: r.slug }} className="flex min-h-11 items-center justify-between gap-3 py-2 font-serif text-[16px] hover:text-clay">
                          {r.label[lang]} <ArrowRight aria-hidden className="size-4 shrink-0" />
                        </Link>
                      ) : (
                        <Link to="/yazilar/$slug" params={{ slug: r.slug }} className="flex min-h-11 items-center justify-between gap-3 py-2 font-serif text-[16px] hover:text-clay">
                          {r.label[lang]} <ArrowRight aria-hidden className="size-4 shrink-0" />
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </InfoCard>
            )}
            <MoreLink to={hubPath}>{lang === "tr" ? "Tüm kavramlar" : "All concepts"}</MoreLink>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <PageSection title={lang === "tr" ? "Aynı kategorideki kavramlar" : "Related concepts"}>
          <ul className="grid divide-y divide-ink/10 border-y border-ink/10 sm:grid-cols-2 sm:gap-x-12 lg:grid-cols-4 lg:divide-y-0">
            {related.map((r) => (
              <li key={r.id} className="py-4">
                <Link to={lang === "tr" ? "/sozluk/$slug" : "/glossary/$slug"} params={{ slug: pilotSlug(r.id, lang) }} className="group block">
                  <span className="font-serif text-[18px] leading-snug text-ink group-hover:text-clay">{r.term[lang]}</span>
                  <span className="mt-1 block text-sm leading-snug text-ink/70">{r.short[lang]}</span>
                </Link>
              </li>
            ))}
          </ul>
        </PageSection>
      )}
    </>
  );
}

export function lexiconDetailHead({
  id,
  lang,
  trSlug,
  enSlug,
}: {
  id: string;
  lang: Lang;
  trSlug: string;
  enSlug: string;
}) {
  const entry = ENTRIES.find((e) => e.id === id);
  if (!entry) return { meta: [{ title: "Not found" }] };

  const base = "https://akillisurdurulebilirlik.com";
  const trUrl = `${base}/sozluk/${trSlug}`;
  // The canonical English home of a concept is the English brand domain.
  // /glossary/<slug> on the Turkish domain is a duplicate, so it canonicalises
  // to /concepts/<slug> and hreflang stays reciprocal with the TR page.
  const conceptSlug = conceptSlugForPilot(trSlug);
  const enUrl = conceptSlug
    ? `https://smartsustainability.ai/concepts/${conceptSlug}`
    : `${base}/glossary/${enSlug}`;
  const url = lang === "tr" ? trUrl : enUrl;

  const title =
    lang === "tr"
      ? pageTitle(`${entry.term.tr} nedir? — Kavram Sözlüğü`, "tr")
      : pageTitle(`What is ${entry.term.en}? — Lexicon`, "en");
  const description = entry.short[lang];

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      ...(lang === "tr" ? [
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...socialImageMeta(),
      ] : []),
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:locale", content: lang === "tr" ? "tr_TR" : "en_US" },
    ],
    links: [
      { rel: "canonical", href: url },
      // Independent sites: no automatic TR–EN hreflang pairing.
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "DefinedTerm",
              "@id": url,
              name: entry.term[lang],
              alternateName: lang === "tr" ? entry.term.en : entry.term.tr,
              description: entry.short[lang],
              inLanguage: lang === "tr" ? "tr-TR" : "en-US",
              url,
              inDefinedTermSet: {
                "@type": "DefinedTermSet",
                name:
                  lang === "tr"
                    ? `${siteName("tr")} — Kavram Sözlüğü`
                    : `${siteName("en")} — Lexicon`,
                url: `${base}${lang === "tr" ? "/sozluk" : "/glossary"}`,
              },
              creator: personRef(),
              isPartOf: {
                "@type": "WebSite",
                name: siteName(lang === "tr" ? "tr" : "en"),
                url: base,
              },
            },

            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: lang === "tr" ? "Ana Sayfa" : "Home",
                  item: `${base}/`,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: lang === "tr" ? "Sözlük" : "Lexicon",
                  item: `${base}${lang === "tr" ? "/sozluk" : "/glossary"}`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: entry.term[lang],
                  item: url,
                },
              ],
            },
          ],
        }),
      },
    ],
  };
}
